import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api, useAuthStore } from '../../lib/auth';
import { toast } from 'sonner';
import { Download, Upload, FileSpreadsheet, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Excel Import/Export dialog for the Planning module.
 *
 * Two import modes:
 *  - Standart (uzun): one row per assignment — generated by /planning/template.
 *  - Pivot (haftalık ızgara): the existing weekly-grid layout the operator
 *    already uses (e.g. "HYK BV - OEVEL - W1" sheets). Operator picks the
 *    customer + depot + owner firm; route codes are matched by code, missing
 *    routes are auto-created on demand.
 *
 * Two export buttons (single mode):
 *  - Bu haftayı indir → /planning/export?year&week
 *  - Şablon indir     → /planning/template
 */
export function ImportExportDialog({ open, onClose, year, week, board, onImported }) {
  const { accessToken } = useAuthStore();
  const [mode, setMode] = useState('pivot');

  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [autoCreateRoutes, setAutoCreateRoutes] = useState(true);
  const [companyId, setCompanyId] = useState('');
  const [depotId, setDepotId] = useState('');
  const [ownerFirmId, setOwnerFirmId] = useState('');
  const [employeeFirmId, setEmployeeFirmId] = useState('');

  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run result

  const companies = board?.companies || [];
  const depots = useMemo(
    () => (board?.depots || []).filter((d) => !companyId || d.company_id === companyId),
    [board?.depots, companyId]
  );
  const firms = board?.firms || [];

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview(null);
      setBusy(false);
    }
  }, [open]);

  // Trigger a download via fetch + Authorization header (bare <a> can't carry it).
  const downloadFile = async (path, fname) => {
    try {
      setBusy(true);
      const res = await axios.get(`${API_URL}/api${path}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('İndirildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İndirme başarısız');
    } finally {
      setBusy(false);
    }
  };

  const submitLong = async () => {
    if (!file) { toast.error('Dosya seçin'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('overwrite', overwrite ? 'true' : 'false');
    setBusy(true);
    try {
      const res = await api.post('/planning/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data?.data || {};
      toast.success(`${d.created || 0} atama içe aktarıldı` + (d.skipped ? `, ${d.skipped} atlandı` : ''));
      setPreview({ kind: 'long', data: d });
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İçe aktarma başarısız');
    } finally {
      setBusy(false);
    }
  };

  const submitPivot = async (dryRun) => {
    if (!file) { toast.error('Dosya seçin'); return; }
    if (!companyId) { toast.error('Müşteri (company) seçin'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const res = await api.post(
        `/planning/import-pivot?company_id=${encodeURIComponent(companyId)}` +
          (depotId ? `&depot_id=${encodeURIComponent(depotId)}` : '') +
          (ownerFirmId ? `&owner_firm_id=${encodeURIComponent(ownerFirmId)}` : '') +
          (employeeFirmId ? `&employee_firm_id=${encodeURIComponent(employeeFirmId)}` : '') +
          `&overwrite=${overwrite}` +
          `&auto_create_routes=${autoCreateRoutes}` +
          `&dry_run=${dryRun}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const d = res.data?.data || {};
      setPreview({ kind: 'pivot', dryRun, data: d });
      if (dryRun) {
        toast.info(`${d.candidates || 0} satır bulundu (önizleme)`);
      } else {
        toast.success(`${d.created || 0} atama içe aktarıldı` + (d.skipped ? `, ${d.skipped} atlandı` : ''));
        onImported?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İçe aktarma başarısız');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="planning-import-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
            Excel İçe / Dışa Aktarma
          </DialogTitle>
          <DialogDescription>
            Planlama tahtasını .xlsx formatında dışa aktarın veya mevcut bir dosyayı içe aktarın.
          </DialogDescription>
        </DialogHeader>

        {/* Export shortcuts */}
        <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-700 pb-3">
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => downloadFile(`/planning/export?year=${year}&week=${week}`, `planlama_${year}_W${String(week).padStart(2, '0')}.xlsx`)}
            data-testid="planning-export-week-btn"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Bu haftayı indir (W{week})
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => downloadFile('/planning/template', 'planlama_sablon.xlsx')}
            data-testid="planning-template-btn"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Standart şablonu indir
          </Button>
        </div>

        {/* Import section */}
        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="pivot" data-testid="planning-import-tab-pivot">
              Pivot (haftalık ızgara)
            </TabsTrigger>
            <TabsTrigger value="long" data-testid="planning-import-tab-long">
              Standart (uzun şablon)
            </TabsTrigger>
          </TabsList>

          {/* PIVOT mode */}
          <TabsContent value="pivot" className="space-y-3 pt-3">
            <div className="text-xs text-slate-500 leading-relaxed">
              Mevcut "HYK BV — OEVEL — W..." gibi haftalık ızgara dosyaları için.
              <br />Müşteri / Depo / İşveren firma seçin — kalan alanlar dosyadan okunur.
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px]">Müşteri (Company) *</Label>
                <Select value={companyId} onValueChange={(v) => { setCompanyId(v); setDepotId(''); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="planning-import-company">
                    <SelectValue placeholder="Seçin..." />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">Depo</Label>
                <Select value={depotId} onValueChange={setDepotId} disabled={!companyId}>
                  <SelectTrigger className="h-8 text-xs" data-testid="planning-import-depot">
                    <SelectValue placeholder="(Opsiyonel)" />
                  </SelectTrigger>
                  <SelectContent>
                    {depots.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">Owner Firm (rota sahibi)</Label>
                <Select value={ownerFirmId} onValueChange={setOwnerFirmId}>
                  <SelectTrigger className="h-8 text-xs" data-testid="planning-import-owner-firm">
                    <SelectValue placeholder="(Opsiyonel)" />
                  </SelectTrigger>
                  <SelectContent>
                    {firms.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">İşveren Firma (çalışan)</Label>
                <Select value={employeeFirmId} onValueChange={setEmployeeFirmId}>
                  <SelectTrigger className="h-8 text-xs" data-testid="planning-import-employee-firm">
                    <SelectValue placeholder="(Opsiyonel)" />
                  </SelectTrigger>
                  <SelectContent>
                    {firms.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[11px]">Excel dosyası (.xlsx)</Label>
              <Input
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                className="h-8 text-xs"
                data-testid="planning-import-file"
              />
            </div>

            <div className="flex flex-wrap gap-3 text-[12px]">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)}
                  data-testid="planning-import-overwrite" />
                Etkilenen haftaları temizle (overwrite)
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={autoCreateRoutes} onChange={(e) => setAutoCreateRoutes(e.target.checked)}
                  data-testid="planning-import-auto-routes" />
                Eksik rotaları otomatik oluştur
              </label>
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                disabled={!file || busy || !companyId}
                onClick={() => submitPivot(true)}
                data-testid="planning-import-pivot-preview"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <AlertCircle className="h-3.5 w-3.5 mr-1.5" />}
                Önizleme (dry-run)
              </Button>
              <Button
                size="sm"
                disabled={!file || busy || !companyId}
                onClick={() => submitPivot(false)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="planning-import-pivot-submit"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                İçe aktar
              </Button>
            </div>
          </TabsContent>

          {/* LONG mode */}
          <TabsContent value="long" className="space-y-3 pt-3">
            <div className="text-xs text-slate-500 leading-relaxed">
              "Şablon İndir" ile aldığınız uzun-format dosya. Her satır bir atama,
              kolon başlıkları sabittir.
            </div>
            <div>
              <Label className="text-[11px]">Excel dosyası (.xlsx)</Label>
              <Input
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                className="h-8 text-xs"
                data-testid="planning-import-long-file"
              />
            </div>
            <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
              <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)}
                data-testid="planning-import-long-overwrite" />
              Etkilenen haftaları temizle (overwrite)
            </label>
            <Button
              size="sm"
              disabled={!file || busy}
              onClick={submitLong}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="planning-import-long-submit"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
              İçe aktar
            </Button>
          </TabsContent>
        </Tabs>

        {/* Preview / Result */}
        {preview && (
          <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50 dark:bg-slate-800/40 space-y-1.5 text-xs max-h-64 overflow-y-auto" data-testid="planning-import-result">
            <div className="font-semibold text-sm flex items-center gap-1.5">
              {preview.dryRun ? <AlertCircle className="h-4 w-4 text-amber-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
              {preview.dryRun ? 'Önizleme' : 'İçe aktarma sonucu'}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <div><span className="text-slate-500">Aday:</span> {preview.data.candidates ?? preview.data.created ?? 0}</div>
              <div><span className="text-slate-500">Atlandı:</span> {preview.data.skipped ?? 0}</div>
            </div>
            {preview.data.affected_weeks?.length > 0 && (
              <div>
                <span className="text-slate-500">Etkilenen haftalar:</span>{' '}
                {preview.data.affected_weeks.map((w) => `${w.year}/W${w.week}`).join(', ')}
              </div>
            )}
            {preview.data.created_routes?.length > 0 && (
              <div>
                <span className="text-slate-500">Yeni rotalar oluşturuldu:</span>{' '}
                {preview.data.created_routes.map((r) => r.route_code).join(', ')}
              </div>
            )}
            {preview.data.unmatched_employees?.length > 0 && (
              <div className="text-amber-700 dark:text-amber-400">
                <span className="font-semibold">Eşleşmeyen çalışanlar:</span>{' '}
                {preview.data.unmatched_employees.map((e) => `${e.name} (×${e.count})`).join(', ')}
              </div>
            )}
            {preview.data.unmatched_routes?.length > 0 && (
              <div className="text-amber-700 dark:text-amber-400">
                <span className="font-semibold">Eşleşmeyen rotalar:</span>{' '}
                {preview.data.unmatched_routes.map((r) => `${r.code} (×${r.count})`).join(', ')}
              </div>
            )}
            {preview.data.errors?.length > 0 && (
              <details>
                <summary className="cursor-pointer text-red-600 dark:text-red-400 font-semibold">
                  {preview.data.errors.length} hata
                </summary>
                <ul className="ml-4 mt-1 list-disc">
                  {preview.data.errors.slice(0, 30).map((e, i) => (
                    <li key={i}>
                      {e.row ? `Satır ${e.row}: ` : ''}{e.error}{e.employee ? ` (${e.employee})` : ''}{e.date ? ` — ${e.date}` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="planning-import-close">
            Kapat
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
